import React, { useEffect } from 'react';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { SmallCloseIcon } from '@chakra-ui/icons';
import { useFormik } from 'formik';

import { validateIpv4List } from '../../Utils/backStageApi';

export type IssuerFormData = {
  name: string;
  acronym: string;
  cert_name: string;
  ips: string;
};

interface Props {
  initData?: IssuerFormData;
  setFormData: React.Dispatch<React.SetStateAction<IssuerFormData | undefined>>;
}

const IssuerEditer: React.FC<Props> = ({ initData, setFormData }) => {
  const validate = (values: IssuerFormData) => {
    const errors: Partial<Record<keyof IssuerFormData, string>> = {};
    if (!values.name.trim()) errors.name = '請填寫名稱';
    if (!values.acronym.trim()) errors.acronym = '請填寫縮寫';
    if (!values.cert_name.trim()) errors.cert_name = '請填寫證書名稱';
    if (!values.ips.trim()) {
      errors.ips = '請填寫 IP 位址';
    } else {
      const ipError = validateIpv4List(values.ips);
      if (ipError) errors.ips = ipError;
    }
    return errors;
  };

  const formik = useFormik<IssuerFormData>({
    initialValues: { name: '', acronym: '', cert_name: '', ips: '' },
    validate,
    onSubmit: () => {},
    enableReinitialize: false,
  });

  useEffect(() => {
    if (initData) formik.setValues(initData);
  }, [initData]);

  useEffect(() => {
    const isValid =
      Object.keys(formik.errors).length === 0 &&
      formik.values.name.trim() !== '' &&
      formik.values.acronym.trim() !== '' &&
      formik.values.cert_name.trim() !== '' &&
      formik.values.ips.trim() !== '';
    setFormData(isValid ? formik.values : undefined);
  }, [formik.values, formik.errors]);

  const fields: { key: keyof IssuerFormData; label: string; placeholder: string; hint?: string }[] = [
    { key: 'name', label: '名稱', placeholder: '請輸入機構名稱' },
    { key: 'acronym', label: '縮寫', placeholder: '請輸入機構縮寫' },
    { key: 'cert_name', label: '證書名稱', placeholder: '請輸入證書名稱' },
    { key: 'ips', label: 'IP', placeholder: '例如：1.2.3.4, 5.6.7.8', hint: '多個 IP 請以逗號分隔' },
  ];

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing="50px">
        {fields.map(({ key, label, placeholder, hint }) => (
          <Flex key={key} gap="30px">
            <Text
              w="140px"
              minW="140px"
              maxW="140px"
              flexShrink={0}
              flexGrow={0}
              whiteSpace="nowrap"
              wordBreak="keep-all"
              textAlign="left"
              fontWeight="600"
              fontSize="24px"
              lineHeight="133%"
            >
              {label}
            </Text>
            <Flex direction="column" w="100%">
              <FormControl isInvalid={!!formik.errors[key] && formik.touched[key]}>
                <InputGroup>
                  <Input
                    type="text"
                    name={key}
                    placeholder={placeholder}
                    size="lg"
                    value={formik.values[key]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.values[key] && (
                    <InputRightElement cursor="pointer" onClick={() => formik.setFieldValue(key, '')}>
                      <SmallCloseIcon color="primary.main" />
                    </InputRightElement>
                  )}
                </InputGroup>
                {hint && !formik.errors[key] && (
                  <Text fontFamily="'Open Sans', sans-serif" fontSize="13px" color="#4F6470" mt="6px">
                    {hint}
                  </Text>
                )}
                <FormErrorMessage>{formik.errors[key]}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Flex>
        ))}
      </Stack>
    </form>
  );
};

export default IssuerEditer;
